import React from 'react';
import cn from 'classnames';

import './textArea.scss';
import NobleIcon from '../../nobleIcon';
import { useRef, useEffect } from 'react';
import { MAX_ALLOWED_MESSAGE_SEGMENTS } from 'constants/validations';
import { describeChar } from 'helpers/analyzeSms';
import { smartEncodeGsm7 } from 'helpers/smartEncodeGsm7';

// True inline highlighting isn't possible inside a native <textarea> (no rich
// text support), so this renders a backdrop <div> behind the textarea with
// the same text and font metrics, wraps the offending characters in <mark>,
// and makes the textarea's own text transparent so only the backdrop shows
// through. This only works cleanly because the textarea has no internal
// scrolling (it auto-grows to fit its content), so the two layers never need
// scroll-position syncing.
function buildHighlightedNodes(value, nonGsmChars) {
	if (!nonGsmChars || nonGsmChars.length === 0) {
		return value;
	}

	const nodes = [];
	let cursor = 0;

	nonGsmChars.forEach(({ char, index }, i) => {
		if (index > cursor) {
			nodes.push(value.slice(cursor, index));
		}
		nodes.push(
			<mark
				className="textArea__backdrop__highlight"
				key={`nonGsm-${index}-${i}`}
				title={`"${describeChar(char)}" is not in the GSM-7 character set`}>
				{char}
			</mark>,
		);
		cursor = index + char.length;
	});

	if (cursor < value.length) {
		nodes.push(value.slice(cursor));
	}

	// A message ending in a newline would otherwise render its trailing empty
	// line at zero height in the backdrop, drifting out of sync with the
	// textarea's own layout.
	if (value.endsWith('\n')) {
		nodes.push(' ');
	}

	return nodes;
}

function summarizeNonGsmChars(nonGsmChars) {
	if (!nonGsmChars || nonGsmChars.length === 0) return '';

	const uniqueChars = [...new Set(nonGsmChars.map(({ char }) => char))];
	const preview = uniqueChars
		.slice(0, 5)
		.map((char) => `"${describeChar(char)}"`)
		.join(', ');
	const more =
		uniqueChars.length > 5 ? `, +${uniqueChars.length - 5} more` : '';

	const count = nonGsmChars.length;
	return (
		`${count} character${count === 1 ? '' : 's'} ` +
		`force${count === 1 ? 's' : ''} UCS-2 encoding ` +
		`(70 chars/segment instead of 160): ${preview}${more}`
	);
}

function TextArea({
	displayLabel,
	id,
	value,
	inputError,
	disabled,
	maxLength,
	showSmsSegmentInfo,
	onSmartEncode,
	...rest
}) {
	const textAreaRef = useRef(null);

	useEffect(() => {
		const el = textAreaRef.current;
		if (!el) return;

		el.style.height = 'auto';
		el.style.height = el.scrollHeight + 'px';
	}, [value]);

	const nonGsmChars = showSmsSegmentInfo?.nonGsmChars || [];
	const hasHighlight = nonGsmChars.length > 0;
	const cleanedValue = showSmsSegmentInfo ? smartEncodeGsm7(value) : value;
	const canSmartEncode = !!showSmsSegmentInfo && cleanedValue !== value;

	const handleSmartEncode = () => {
		if (!canSmartEncode || !onSmartEncode) return;
		onSmartEncode(cleanedValue);
	};

	return (
		<div className="textArea">
			{displayLabel && (
				<label className="textArea__label" htmlFor={id}>
					{displayLabel}
				</label>
			)}
			<div className="textArea__inputContainer">
				<div
					className={cn('textArea__editor', {
						'textArea__editor-highlighted': !!showSmsSegmentInfo,
					})}>
					{showSmsSegmentInfo && (
						<div
							key="textarea-backdrop"
							className="textArea__backdrop"
							aria-hidden="true">
							{buildHighlightedNodes(value, nonGsmChars)}
						</div>
					)}
					<textarea
						key="textarea-input"
						ref={textAreaRef}
						maxLength={maxLength}
						value={value}
						className={cn([
							'textArea__input',
							{ 'textArea__input-error': inputError },
							{ 'textArea__input-transparent': !!showSmsSegmentInfo },
						])}
						disabled={disabled && 'disabled'}
						{...rest}>
					</textarea>
				</div>

				{disabled && (
					<div className="textArea__icon">
						<NobleIcon name="lock" />
					</div>
				)}

				{maxLength && showSmsSegmentInfo?.perSegment !== 160 && (
					<div
						className={cn('textArea__input__maxLength', {
							'textArea__input__maxLength-error': value.length === maxLength,
						})}>
						{value.length} / {maxLength}
					</div>
				)}
			</div>
			{showSmsSegmentInfo && (
				<div className="textArea__sms-segment-info">
					<div className="textArea__sms-segment-info__content">
						<span>Encoding: {showSmsSegmentInfo.encoding}</span>
						<span>
							Length: {showSmsSegmentInfo.totalLength}/
							{showSmsSegmentInfo?.perSegment}
						</span>
						<span>
							Segments: {showSmsSegmentInfo?.segments}/
							{MAX_ALLOWED_MESSAGE_SEGMENTS}
						</span>
					</div>

					{hasHighlight && (
						<div className="textArea__sms-segment-info__warning">
							<span className="textArea__sms-segment-info__warning-text">
								{summarizeNonGsmChars(nonGsmChars)}
							</span>
							{canSmartEncode && (
								<button
									type="button"
									className="textArea__sms-segment-info__smart-encode-btn"
									onClick={handleSmartEncode}>
									Convert to GSM-7-compatible text
								</button>
							)}
						</div>
					)}

					{inputError && (
						<div className="textArea__input-message-error">
							<span>{inputError}</span>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

export default TextArea;
